import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { marketingService } from '../../../services/marketingService';

const OnlineBookingManagement = ({ expanded = false }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    loadBookings();
  }, [selectedStatus]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await marketingService?.getBookings(selectedStatus === 'all' ? null : selectedStatus);
      setBookings(data);
    } catch (err) {
      setError(err?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const bookingStats = {
    total: bookings?.length || 0,
    confirmed: bookings?.filter(b => b?.status === 'confirmed')?.length || 0,
    pending: bookings?.filter(b => b?.status === 'pending')?.length || 0,
    cancelled: bookings?.filter(b => b?.status === 'cancelled')?.length || 0
  };

  const bookingSources = [
    { name: 'Website', count: bookings?.filter(b => b?.bookingSource === 'Website')?.length || 0 },
    { name: 'Google', count: bookings?.filter(b => b?.bookingSource === 'Google')?.length || 0 },
    { name: 'Facebook', count: bookings?.filter(b => b?.bookingSource === 'Facebook')?.length || 0 },
    { name: 'Other', count: bookings?.filter(b => !['Website', 'Google', 'Facebook']?.includes(b?.bookingSource))?.length || 0 }
  ];

  const displayBookings = expanded ? bookings : bookings?.slice(0, 5);

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Calendar" size={20} className="text-primary" />
          Online Booking Management
        </h2>
        <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
          New Booking
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-muted/50 rounded-lg p-3 border border-border">
          <p className="text-xs text-muted-foreground mb-1">Total Bookings</p>
          <p className="text-xl font-bold text-foreground">{bookingStats?.total}</p>
        </div>
        <div className="bg-success/10 rounded-lg p-3 border border-success/20">
          <p className="text-xs text-success mb-1">Confirmed</p>
          <p className="text-xl font-bold text-success">{bookingStats?.confirmed}</p>
        </div>
        <div className="bg-warning/10 rounded-lg p-3 border border-warning/20">
          <p className="text-xs text-warning mb-1">Pending</p>
          <p className="text-xl font-bold text-warning">{bookingStats?.pending}</p>
        </div>
        <div className="bg-error/10 rounded-lg p-3 border border-error/20">
          <p className="text-xs text-error mb-1">Cancelled</p>
          <p className="text-xl font-bold text-error">{bookingStats?.cancelled}</p>
        </div>
      </div>
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Booking Sources</h3>
        <div className="space-y-2">
          {bookingSources?.map((source, index) => {
            const percentage = bookingStats?.total > 0 ? ((source?.count / bookingStats?.total) * 100)?.toFixed(1) : 0;
            return (
              <div key={index} className="flex items-center gap-3">
                <span className="text-sm text-foreground w-20">{source?.name}</span>
                <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary h-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <span className="text-sm font-semibold text-foreground w-16 text-right">
                  {source?.count} ({percentage}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="bg-error/10 border border-error rounded-lg p-3">
          <p className="text-error text-sm">{error}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Recent Bookings</h3>
          {displayBookings?.map((booking) => (
            <div key={booking?.id} className="bg-muted/30 rounded-lg p-3 border border-border hover:border-primary transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="font-semibold text-foreground">{booking?.patientName}</p>
                  <p className="text-xs text-muted-foreground">{booking?.appointmentType}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    booking?.status === 'confirmed' ?'bg-success/10 text-success'
                      : booking?.status === 'pending' ?'bg-warning/10 text-warning' :'bg-error/10 text-error'
                  }`}
                >
                  {booking?.status}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name="Calendar" size={12} />
                  {new Date(booking?.appointmentDate)?.toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Globe" size={12} />
                  {booking?.bookingSource}
                </span>
              </div>
            </div>
          ))}
          {!expanded && bookings?.length > 5 && (
            <Button variant="ghost" size="sm" fullWidth>
              View All Bookings ({bookings?.length})
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default OnlineBookingManagement;